"use client";

import { c as _c } from "react/compiler-runtime";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { createContext, use, useCallback, useMemo, useReducer } from "react";
function createDefaultValuesStore() {
  const store: Record<string, string[]> = {};
  return {
    register: (filterId: string, values: string[]) => {
      if (!(filterId in store)) {
        store[filterId] = values;
      }
    },
    get: (filterId: string) => store[filterId],
    getAll: () => store
  };
}
type FilterAction = {
  type: "SET_FILTER";
  filterId: string;
  values: string[];
} | {
  type: "RESET_ALL_FILTERS";
  defaultValues: Record<string, string[]>;
} | {
  type: "SYNC_WITH_URL";
  urlFilters: Record<string, string[]>;
} | {
  type: "APPLY_FILTERS";
};
interface FilterState {
  appliedFilters: Record<string, string[]>;
  pendingFilters: Record<string, string[]>;
}
function filterReducer(state: FilterState, action: FilterAction): FilterState {
  switch (action.type) {
    case "SET_FILTER":
      return {
        ...state,
        pendingFilters: {
          ...state.pendingFilters,
          [action.filterId]: action.values
        }
      };
    case "RESET_ALL_FILTERS":
      return {
        appliedFilters: {
          ...action.defaultValues
        },
        pendingFilters: {
          ...action.defaultValues
        }
      };
    case "SYNC_WITH_URL":
      return {
        appliedFilters: action.urlFilters,
        pendingFilters: {
          ...state.pendingFilters
        }
      };
    case "APPLY_FILTERS":
      return {
        ...state,
        appliedFilters: {
          ...state.pendingFilters
        },
        pendingFilters: {
          ...state.pendingFilters
        }
      };
    default:
      return state;
  }
}
interface FilterContextType {
  getFilterValues: (filterId: string) => string[];
  setFilterValues: (filterId: string, values: string[]) => void;
  resetAllFilters: () => void;
  pendingFilters: Record<string, string[]>;
  applyFilters: () => void;
  hasPendingChanges: boolean;
  registerDefaultValue: (filterId: string, defaultValue: string[]) => void;
}
const FilterContext = createContext<FilterContextType | undefined>(undefined);
interface FilterProviderProps {
  children: React.ReactNode;
}
export function FilterProvider(t0) {
  const $ = _c(32);
  const {
    children
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = createDefaultValuesStore();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const defaultValuesStore = t1;
  let urlFilters;
  if ($[1] !== searchParams) {
    urlFilters = {};
    searchParams.forEach((value, key) => {
      if (urlFilters[key]) {
        urlFilters[key].push(value);
      } else {
        urlFilters[key] = [value];
      }
    });
    $[1] = searchParams;
    $[2] = urlFilters;
  } else {
    urlFilters = $[2];
  }
  const initialFilters = urlFilters;
  let t2;
  if ($[3] !== initialFilters) {
    t2 = {
      appliedFilters: initialFilters,
      pendingFilters: initialFilters
    };
    $[3] = initialFilters;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const [state, dispatch] = useReducer(filterReducer, t2);
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = (filterId, values) => {
      defaultValuesStore.register(filterId, values);
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const registerDefaultValue = t3;
  let t4;
  if ($[6] !== state.appliedFilters || $[7] !== state.pendingFilters) {
    const defaultValues = defaultValuesStore.getAll();
    t4 = Object.entries(state.pendingFilters).some(t5 => {
      const [filterId_0, values_0] = t5;
      let applied = state.appliedFilters[filterId_0] ?? [];
      if (applied.length === 0) {
        applied = defaultValues[filterId_0] ?? [];
      }
      if (values_0.length !== applied.length) {
        return true;
      }
      return !values_0.every(v_0 => applied.includes(v_0)) || !applied.every(v => values_0.includes(v));
    });
    $[6] = state.appliedFilters;
    $[7] = state.pendingFilters;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const hasPendingChanges = t4;
  let t5;
  if ($[9] !== state.appliedFilters || $[10] !== state.pendingFilters) {
    t5 = filterId_1 => {
      if (state.pendingFilters[filterId_1] !== undefined) {
        return state.pendingFilters[filterId_1];
      }
      const appliedValues = state.appliedFilters[filterId_1] ?? [];
      return appliedValues.length > 0 ? appliedValues : defaultValuesStore.get(filterId_1) ?? [];
    };
    $[9] = state.appliedFilters;
    $[10] = state.pendingFilters;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const getFilterValues = t5;
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = (filterId_2, values_1) => {
      dispatch({
        type: "SET_FILTER",
        filterId: filterId_2,
        values: values_1
      });
    };
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const setFilterValues = t6;
  let t7;
  if ($[13] !== pathname || $[14] !== router || $[15] !== searchParams) {
    t7 = () => {
      dispatch({
        type: "RESET_ALL_FILTERS",
        defaultValues: {
          ...defaultValuesStore.getAll()
        }
      });
      const queryParam = searchParams.get("q");
      if (queryParam) {
        router.push(`${pathname}?q=${encodeURIComponent(queryParam)}`);
      } else {
        router.push(pathname);
      }
    };
    $[13] = pathname;
    $[14] = router;
    $[15] = searchParams;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const resetAllFilters = t7;
  let t8;
  if ($[17] !== hasPendingChanges || $[18] !== pathname || $[19] !== router || $[20] !== searchParams || $[21] !== state.pendingFilters) {
    t8 = () => {
      if (!hasPendingChanges) {
        return;
      }
      dispatch({
        type: "APPLY_FILTERS"
      });
      const params = new URLSearchParams(searchParams.toString());
      Object.keys(state.pendingFilters).forEach(filterId_3 => {
        params.delete(filterId_3);
      });
      const defaultValues_0 = defaultValuesStore.getAll();
      Object.entries(state.pendingFilters).forEach(t9 => {
        const [filterId_4, values_2] = t9;
        const defaultVal = defaultValues_0[filterId_4] ?? [];
        const isEqualToDefault = values_2.length === defaultVal.length && values_2.every(v_2 => defaultVal.includes(v_2)) && defaultVal.every(v_1 => values_2.includes(v_1));
        if (!isEqualToDefault) {
          if (values_2.length > 0) {
            values_2.forEach(value_0 => {
              params.append(filterId_4, value_0);
            });
          } else {
            if (defaultVal.length > 0) {
              params.append(filterId_4, "");
            }
          }
        }
      });
      router.push(`${pathname}?${params.toString()}`);
    };
    $[17] = hasPendingChanges;
    $[18] = pathname;
    $[19] = router;
    $[20] = searchParams;
    $[21] = state.pendingFilters;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const applyFilters = t8;
  let t9;
  if ($[23] !== applyFilters || $[24] !== getFilterValues || $[25] !== hasPendingChanges || $[26] !== resetAllFilters || $[27] !== state.pendingFilters) {
    t9 = {
      getFilterValues,
      setFilterValues,
      resetAllFilters,
      pendingFilters: state.pendingFilters,
      applyFilters,
      hasPendingChanges,
      registerDefaultValue
    };
    $[23] = applyFilters;
    $[24] = getFilterValues;
    $[25] = hasPendingChanges;
    $[26] = resetAllFilters;
    $[27] = state.pendingFilters;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const value_1 = t9;
  let t10;
  if ($[29] !== children || $[30] !== value_1) {
    t10 = <FilterContext value={value_1}>{children}</FilterContext>;
    $[29] = children;
    $[30] = value_1;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
}
export function useFilter() {
  const context = use(FilterContext);
  if (context === undefined) {
    throw new Error("useFilter must be used within a FilterProvider");
  }
  return context;
}